import React, { useState } from "react";
import { CrossIcon } from "@100mslive/react-icons";
import { Box, Button, Dialog, IconButton, Text } from "@100mslive/react-ui";
import AndroidPermissions from "../../images/android-permissions.png";
import ChromeLogo from "../../images/chrome.png";
import IosPermissions from "../../images/ios-permissions.png";
import NetworkSignal from "../../images/network-signal.png";
import RecordingInstructions from "../../images/recording-instructions.png";
import warning1 from "../../images/warning-1.svg";
import warning2 from "../../images/warning-2.svg";
import warning3 from "../../images/warning-3.svg";
import warning4 from "../../images/warning-4.svg";
import warning5 from "../../images/warning-5.svg";
import WarningIcon from "../../images/warning-icon.svg";
import { isIOS } from "../../common/constants";

const WARNING_MESSAGES = [
  {
    img: warning1,
    eng: "Ensure that you are fully dressed and proper lighting is present.",
    hi: "(सुनिश्चित करें कि आप पूरी तरह से तैयार हैं और उचित रोशनी मौजूद है)",
  },
  {
    img: warning2,
    eng: "The call will not involve a physical examination unless the doctor says so ",
    hi: "(यदि डॉक्टर नहीं कहते हैं तो कॉल में शारीरिक परीक्षण शामिल नहीं होगा)",
  },
  {
    img: warning3,
    eng: "Maintain respectful language during the call.",
    hi: "(कॉल के दौरान सम्मानजनक भाषा बनाए रखें)",
  },
  {
    img: warning4,
    eng: "Refrain from engaging in masturbation and self-pleasure.",
    hi: "(हस्तमैथुन और आत्म-आनन्द में संलग्न होने से बचें)",
  },
  {
    img: warning5,
    eng: "Do not involve in flashing",
    hi: "(फ्लैशिंग में शामिल नहीं होना है)",
  },
];

const InstructionsModal = ({
  open,
  onOpenChange,
  recordingPermission = false,
  onComplete,
  children,
}) => {
  const [showWarnings, setShowWarnings] = useState(false);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isLastIndex, setIsLastIndex] = useState(false);

  const handleContinueToWarnings = () => {
    setShowWarnings(true);
  };

  const handleNextCard = () => {
    const nextIndex = (currentCardIndex + 1) % WARNING_MESSAGES.length;
    setCurrentCardIndex(nextIndex);
    setIsLastIndex(nextIndex === WARNING_MESSAGES.length - 1);
  };

  const handleClose = () => {
    setShowWarnings(false);
    setCurrentCardIndex(0);
    setIsLastIndex(false);
    onOpenChange(false);
    if (onComplete) {
      onComplete();
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay
          css={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }}
          onClick={handleClose}
        />
        <Dialog.Content
          id="instructions-modal"
          css={{
            position: "fixed",
            bottom: 0,
            w: "100%",
            height: showWarnings ? "min(536px)" : "67vh",
            maxHeight: showWarnings ? "536px" : "67vh",
            maxWidth: "410px",
            p: 0,
            r: "$4 $4 0 0",
            overflowY: "auto",
            backgroundColor: "$surfaceDefault",
            zIndex: 1000,
            marginTop: showWarnings ? "200px" : "150px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <IconButton
            css={{
              position: "absolute",
              right: "$4",
              top: "$4",
              cursor: "pointer",
              zIndex: 1002,
              backgroundColor: "white",
              borderRadius: "50%",
              border: "1px solid #ccc",
              padding: "$2",
              width: "30px",
              height: "30px",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
            onClick={handleClose}
            data-testid="dialog_cross_icon"
          >
            <CrossIcon style={{ color: "black" }} />
          </IconButton>

          {!showWarnings ? (
            <>
              <Box css={{ mt: "$10", flex: 1, overflowY: "auto" }}>
                <div>
                  <div></div>
                  <Text css={{ m: "$10" }} variant="h6">
                    Important Steps
                  </Text>
                </div>
                <Text css={{ mx: "$10" }}>
                  1. Grant audio and video permissions for your respective
                  device.
                </Text>
                <Box css={{ m: "$10 $20 $10 $10" }}>
                  <img
                    width="max(100px, 80%)"
                    src={isIOS ? IosPermissions : AndroidPermissions}
                    alt="permission-img"
                  />
                </Box>
                <Text css={{ m: "$10" }}>
                  2. Switch to Chrome for a better experience.
                </Text>
                <Box css={{ mx: "$10" }}>
                  <img src={ChromeLogo} alt="chrome-img" />
                </Box>
                <Text css={{ m: "$10" }}>
                  3. Switch to a better network - video quality will be adjusted
                  to avoid audio disruption.
                </Text>
                <Box css={{ m: "$10 $20 $10 $10" }}>
                  <img src={NetworkSignal} alt="network-img" />
                </Box>
                {recordingPermission && (
                  <>
                    <Text css={{ mx: "$10" }}>
                      4. All Allo sessions are recorded for best consultation
                      experience -
                    </Text>
                    <Text css={{ mx: "$10" }}>
                      100% confidential and secure.
                    </Text>
                    <Text css={{ mx: "$10" }}>
                      Disable/enable this anytime under 'Settings'.
                    </Text>
                    <Box css={{ m: "$10 $20 $16 $10" }}>
                      <img src={RecordingInstructions} alt="chrome-img" />
                    </Box>
                  </>
                )}
              </Box>
              <Box
                css={{
                  flexShrink: 0,
                  backgroundColor: "$surfaceDefault",
                  padding: "$4",
                  borderTop: "1px solid $borderMedEmp",
                }}
              >
                <Button
                  variant="standard"
                  css={{
                    flexShrink: 0,
                    p: "$6 $9",
                    w: "100%",
                    "@md": {
                      w: "100%",
                    },
                    borderRadius: "25px",
                    backgroundColor: "#222222",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "16px",
                    lineHeight: "24px",
                    letterSpacing: "0.15px",
                  }}
                  onClick={handleContinueToWarnings}
                >
                  Continue
                </Button>
              </Box>
            </>
          ) : (
            <Box css={{ position: "relative", height: "100%" }}>
              <div>
                <img
                  width="max(100px, 80%)"
                  src={WarningIcon}
                  alt="permission-img"
                />
              </div>
              <Box>
                <ol start={currentCardIndex + 1}>
                  <li>
                    <Text
                      css={{ mx: "$10", my: "$4" }}
                      style={{
                        fontWeight: "bold",
                      }}
                    >
                      {currentCardIndex + 1}.{"  "}
                      {WARNING_MESSAGES[currentCardIndex]?.eng}
                    </Text>
                    <Text
                      css={{ mx: "$10" }}
                      style={{
                        fontWeight: "semibold",
                      }}
                    >
                      {WARNING_MESSAGES[currentCardIndex]?.hi}
                    </Text>
                    <div
                      style={{
                        margin: "20px",
                      }}
                    >
                      <img
                        width="max(100px, 90%)"
                        src={WARNING_MESSAGES[currentCardIndex]?.img}
                        alt="warning-img"
                      />
                    </div>
                  </li>
                </ol>
              </Box>
              <Box
                css={{
                  position: "fixed",
                  bottom: "0",
                  left: "0",
                  right: "0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "full-width",
                  backgroundColor: "$surfaceDefault",
                  padding: "$4 $4",
                  marginBottom: "10px",
                  zIndex: 1001,
                  "@md": {
                    width: "100%",
                  },
                }}
              >
                {!isLastIndex ? (
                  <Button
                    variant="standard"
                    css={{
                      backgroundColor: "#222222",
                      borderRadius: "25px",
                      width: "100%",
                      padding: "10px 20px",
                      "@md": {
                        width: "100%",
                      },
                    }}
                    onClick={handleNextCard}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    variant="standard"
                    css={{
                      backgroundColor: "#222222",
                      borderRadius: "25px",
                      width: "100%",
                      padding: "10px 20px",
                      "@md": {
                        width: "100%",
                      },
                    }}
                    onClick={handleClose}
                  >
                    Continue
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default InstructionsModal;
